// src/features/billing/billing-api.ts
import { apiClient } from '@/shared/api/client'
import type { ApiCollection, ApiResource } from '@/shared/types/api'
import type {
  Invoice,
  PaymentMethod,
  Plan,
  Subscription,
  TokenPack,
  UsageSnapshot,
} from '@/shared/types/billing'

export async function listPlans(): Promise<Plan[]> {
  const r = await apiClient.get<ApiCollection<Plan>>('/billing/plans')
  return r.data.data
}

export async function listTokenPacks(): Promise<TokenPack[]> {
  const r = await apiClient.get<ApiCollection<TokenPack>>('/billing/token-packs')
  return r.data.data
}

// suppressErrorToast: фоновый опрос после оплаты не должен сыпать тосты каждые 2 с при сбое.
interface QuietOptions {
  suppressErrorToast?: boolean
}

export async function getSubscription(options: QuietOptions = {}): Promise<Subscription | null> {
  const r = await apiClient.get<ApiResource<Subscription>>('/billing/subscription', {
    suppressErrorToast: options.suppressErrorToast,
  })
  return r.data?.data ?? null
}

export async function subscribe(
  planCode: string,
  paymentMethodId: string | null,
  planPriceId?: string,
): Promise<{ invoice_id: string; confirmation_url: string | null }> {
  const r = await apiClient.post('/billing/subscriptions', {
    plan_code: planCode,
    payment_method_id: paymentMethodId,
    ...(planPriceId !== undefined && { plan_price_id: planPriceId }),
  })
  return r.data
}

export async function cancelSubscription(atPeriodEnd: boolean = true): Promise<Subscription> {
  const r = await apiClient.post<ApiResource<Subscription>>('/billing/subscriptions/cancel', {
    at_period_end: atPeriodEnd,
  })
  return r.data.data
}

export async function resumeSubscription(): Promise<Subscription> {
  const r = await apiClient.post<ApiResource<Subscription>>('/billing/subscriptions/resume')
  return r.data.data
}

export async function topup(
  packCode: string,
  paymentMethodId: string | null,
): Promise<{ invoice_id: string; confirmation_url: string | null }> {
  const r = await apiClient.post('/billing/topups', {
    token_pack_code: packCode,
    payment_method_id: paymentMethodId,
  })
  return r.data
}

export async function listInvoices(
  params?: { kind?: string; status?: string },
  options: QuietOptions = {},
): Promise<Invoice[]> {
  const r = await apiClient.get<ApiCollection<Invoice>>('/billing/invoices', {
    params,
    suppressErrorToast: options.suppressErrorToast,
  })
  return r.data.data
}

export async function getUsage(options: QuietOptions = {}): Promise<UsageSnapshot> {
  const r = await apiClient.get<ApiResource<UsageSnapshot>>('/billing/usage', {
    suppressErrorToast: options.suppressErrorToast,
  })
  return r.data.data
}

export async function listPaymentMethods(): Promise<PaymentMethod[]> {
  const r = await apiClient.get<ApiCollection<PaymentMethod>>('/billing/payment-methods')
  return r.data.data
}

export async function deletePaymentMethod(id: string): Promise<void> {
  await apiClient.delete(`/billing/payment-methods/${id}`)
}

export async function downloadInvoiceDocument(url: string, filename: string): Promise<void> {
  const r = await apiClient.get(url, { responseType: 'blob' })
  const blobUrl = URL.createObjectURL(r.data as Blob)
  const a = document.createElement('a')
  a.href = blobUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(blobUrl)
}
